import { ReactElement, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { useFormik } from 'formik'
import * as yup from 'yup'
import axios, { pluckData } from 'src/utilities/http-client'
import {
  ContentType,
  contentTypes,
  FileData,
} from 'src/v3/utilities/filesystem'
import {
  CancelIcon,
  SubmitIcon,
  UploadIcon,
  UploadFileIcon,
} from 'src/v3/core/icons'

interface ExternalCertificateUploadModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  onError: (message: string) => void
}

let updateExternalCertificateImage = (data: {
  id: string
  imageType: string
}) =>
  axios
    .put<string>(`/api/v2/profile/external-certificate-image`, data)
    .then(pluckData)

export default function ExternalCertificateUploadModal({
  open,
  onClose,
  onSuccess,
  onError,
}: ExternalCertificateUploadModalProps): ReactElement {
  let [saving, setSaving] = useState<boolean>(false)
  let [fileToUpload, setFileToUpload] = useState<FileData | undefined>(
    undefined,
  )

  let form = useFormik({
    validationSchema: yup.object({
      name: yup.string().required(),
      courseCode: yup.string().required(),
      length: yup.number().not([0]).required(),
      hourType: yup.string().not(['unselected']).required(),
      courseType: yup.string().required(),
      dateCompleted: yup.string().required(),
    }),
    enableReinitialize: true,
    initialValues: {
      name: '',
      courseCode: '',
      length: 0,
      hourType: 'unselected',
      courseType: 'recorded',
      dateCompleted: null,
    },
    onSubmit: (values) => {
      setSaving(true)
      axios
        .post(`/api/v2/profile/external-certificate`, {
          name: values.name,
          courseCode: values.courseCode,
          length: values.length,
          hourType: values.hourType,
          courseType: values.courseType,
          dateCompleted: values.dateCompleted,
        })
        .then(({ data }) =>
          updateExternalCertificateImage({
            id: data.id,
            imageType:
              fileToUpload?.extension && fileToUpload?.extension === 'jpeg'
                ? 'jpg'
                : fileToUpload!.extension,
          }),
        )
        .then((response) =>
          axios.put(response, fileToUpload?.file, {
            headers: {
              'Cache-Control': 'max-age=2592000',
              'content-type': fileToUpload?.type,
            },
          }),
        )
        .then(() => {
          onSuccess()
          onClose()
          setSaving(false)
          form.resetForm()
        })
        .catch((err) => {
          onError(err?.message || 'Error Creating External Certificate')
          setSaving(false)
        })
    },
  })

  let handleCapture = (event: React.ChangeEvent) => {
    let file = (event.target as HTMLInputElement).files?.[0]
    let fileName = file?.name || ''
    let reader = new FileReader()

    // eslint-disable-next-line fp/no-mutation
    reader.onloadend = () => {
      let fileData = reader.result
        ?.toString()
        ?.match(/data:[a-z]+\/[a-z]+/)?.[0]
      if (!fileData) {
        onError('Certificate Upload Failed: Read File Failed')
        return
      }

      let fileType = fileData.split(':')[1]
      let fileExtension = fileType.split('/')[1]

      if (!contentTypes.includes(fileType as ContentType)) {
        onError(`Invalid File Type ${fileType}`)
        return
      }

      let binary = atob(reader.result?.toString().split(',')[1] as string)
      let array = Array.from({ length: binary.length }, (_, i) =>
        binary.charCodeAt(i),
      )
      let blobData = new Blob([new Uint8Array(array)], { type: fileExtension })

      setFileToUpload({
        name: fileName,
        type: fileType,
        extension: fileExtension,
        file: blobData,
      })
    }

    reader.readAsDataURL(file!)
  }

  useEffect(() => {
    if (!open) {
      setFileToUpload(undefined)
      form.resetForm()
    }
  }, [open])

  return (
    <Dialog open={open} onClose={onClose}>
      <Card
        sx={{
          width: 300,
          px: 2,
          py: 1,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <form onSubmit={form.handleSubmit}>
          <DialogTitle sx={{ textAlign: 'center', mb: -1.5 }}>
            Upload External Certificate
          </DialogTitle>
          <DialogContent
            sx={{
              px: 1,
              pb: 2,
              display: 'flex',
              gap: 1.5,
              flexDirection: 'column',
            }}
          >
            <TextField
              sx={{ mt: 1, bgcolor: 'background.paper' }}
              variant="outlined"
              size="small"
              label="Name"
              name="name"
              type="text"
              fullWidth
              value={form.values.name}
              onChange={form.handleChange}
              error={form.touched.name && !!form.errors.name}
            />
            <TextField
              sx={{ bgcolor: 'background.paper' }}
              variant="outlined"
              size="small"
              label="Length"
              name="length"
              type="number"
              fullWidth
              value={form.values.length}
              onChange={form.handleChange}
              error={form.touched.length && !!form.errors.length}
            />
            <TextField
              sx={{ bgcolor: 'background.paper' }}
              variant="outlined"
              size="small"
              label="Course Code"
              name="courseCode"
              type="text"
              fullWidth
              value={form.values.courseCode}
              onChange={form.handleChange}
              error={form.touched.courseCode && !!form.errors.courseCode}
            />
            <div className="flex justify-center">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  sx={{ bgcolor: 'background.paper' }}
                  label="Date Completed"
                  value={dayjs(form.values.dateCompleted)}
                  onChange={(val) =>
                    form.setFieldValue(
                      'dateCompleted',
                      val ? dayjs(val).format('MM/DD/YYYY') : '',
                    )
                  }
                  slotProps={{
                    textField: {
                      size: 'small',
                      error:
                        !!form.submitCount &&
                        form.touched.dateCompleted &&
                        !!form.errors.dateCompleted,
                    },
                  }}
                />
              </LocalizationProvider>
            </div>
            <FormControl fullWidth>
              <InputLabel>Hour Type</InputLabel>
              <Select
                sx={{ bgcolor: 'background.paper' }}
                name="hourType"
                value={form.values.hourType}
                label="Hour Type"
                onChange={form.handleChange}
                variant="outlined"
                size="small"
                error={form.touched.hourType && !!form.errors.hourType}
              >
                <MenuItem value="unselected">Unselected</MenuItem>
                {[
                  'Airway Respiratory Ventilation',
                  'Medical',
                  'Trauma',
                  'Operations',
                  'Critical Care',
                  'OBGYN',
                  'Pediatrics Neonatal',
                  'Other',
                ].map((t) => (
                  <MenuItem key={t} value={t.replace(/ /g, '').toLowerCase()}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel sx={{ m: 0, textSizeAdjust: 20 }}>
                Course Type
              </FormLabel>
              <RadioGroup
                row
                name="courseType"
                value={form.values.courseType}
                onChange={form.handleChange}
              >
                <FormControlLabel
                  sx={{ m: 0 }}
                  value="recorded"
                  control={<Radio />}
                  label="Recorded"
                />
                <FormControlLabel
                  sx={{ m: 0 }}
                  value="live"
                  control={<Radio />}
                  label="Live"
                />
              </RadioGroup>
            </FormControl>
            <Box>
              <label style={{ marginTop: -8 }}>
                <Button
                  component="span"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                >
                  <input
                    accept="image/*,.pdf"
                    style={{ display: 'none' }}
                    type="file"
                    onChange={(e) => handleCapture(e)}
                  />
                  Select File
                </Button>
              </label>
              {fileToUpload && (
                <Typography variant="subtitle1">
                  <UploadFileIcon
                    color="primary"
                    sx={{
                      mr: '2px',
                      mb: '2px',
                    }}
                  />
                  {fileToUpload.name}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions className="flex justify-center pt-0 pb-3 mt-0">
            <Button
              color="primary"
              type="submit"
              variant="contained"
              sx={{
                flex: '1 1 0px',
              }}
              startIcon={<SubmitIcon />}
              loading={saving}
              disabled={!fileToUpload}
            >
              Submit
            </Button>
            <Button
              color="error"
              onClick={onClose}
              variant="contained"
              sx={{
                flex: '1 1 0px',
              }}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
      </Card>
    </Dialog>
  )
}
